import styled, { css, keyframes } from 'styled-components';
import Button from '../../../../../components/Button';

export const Container = styled.div`
  width: 80vw;
  position: relative;
`;

export const TableContainerStyle = styled.div`
  height: 70vh;
  overflow: auto;
  table {
    thead {
      position: sticky;
      top: 0px;
      z-index: 100;
      background: #f8f9fa;
      box-shadow:
        0 -1px 0 #f1f3f5,
        0 1px 0 #f1f3f5;
    }
  }
`;

export const CloseStyle = styled.div`
  position: absolute;
  display: flex;
  right: 0;
  top: 0;
  cursor: pointer;
`;

export const Title = styled.p`
  color: #25262b;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  margin-bottom: 30px;
`;

export const ErrorMessageStyle = styled.p`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const DescriptionStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const ProgressCellStyle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 130px;
`;

export const ProgressTrackStyle = styled.div`
  width: 76px;
  height: 6px;
  border-radius: 999px;
  background: #e9ecef;
  overflow: hidden;
`;

/* A run the backend reports no number for. The bar still has to look like it is moving — the row is
   alive — but it must not claim a position, so it sweeps instead of filling. */
const barSweep = keyframes`
  from { background-position: 200% 0; }
  to { background-position: -100% 0; }
`;

export const ProgressFillStyle = styled.div`
  width: ${({ $percentage }) => $percentage}%;
  height: 100%;
  border-radius: inherit;
  background: ${({ $percentage, $slow }) =>
    $slow ? '#F08C00' : $percentage === 100 ? '#2F9E44' : '#228BE6'};
  transition:
    width 500ms ease,
    background 200ms ease;

  ${({ $indeterminate, $slow }) =>
    $indeterminate &&
    css`
      width: 100%;
      background: linear-gradient(
        90deg,
        #e9ecef 0%,
        ${$slow ? '#F08C00' : '#228BE6'} 40%,
        ${$slow ? '#F08C00' : '#228BE6'} 60%,
        #e9ecef 100%
      );
      background-size: 300% 100%;
      animation: ${barSweep} 1.8s linear infinite;
      transition: none;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
        background: #ced4da;
      }
    `}
`;

export const ProgressTextStyle = styled.span`
  color: #495057;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
`;

export const ConfidenceButtonStyle = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  color: ${({ $score }) =>
    $score == null ? '#868E96' : $score >= 85 ? '#2F9E44' : $score >= 70 ? '#F08C00' : '#E03131'};
  cursor: pointer;
  font-size: 13px;
  line-height: 18px;
  font-weight: 700;
  text-decoration: underline;

  &:disabled {
    color: #868e96;
    cursor: default;
    text-decoration: none;
  }
`;

export const DetailsModalBodyStyle = styled.div`
  position: relative;
  width: min(960px, 86vw);
  max-height: 78vh;
  overflow: auto;
  padding: 4px 4px 0;
`;

export const DetailsHeaderStyle = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 20px;
  align-items: center;
  padding: 20px;
  margin-top: 12px;
  border: 1px solid #e7f5ff;
  border-radius: 16px;
  background: linear-gradient(135deg, #f8fbff 0%, #eef8ff 100%);
`;

export const ScoreRingStyle = styled.div`
  width: 116px;
  height: 116px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at center, #fff 58%, transparent 59%),
    conic-gradient(${({ $color, $score }) => $color} ${({ $score }) => $score}%, #dee2e6 0);
  box-shadow: 0 8px 24px rgba(34, 139, 230, 0.12);
`;

export const StatusPillStyle = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $tone }) => $tone?.background || '#f1f3f5'};
  color: ${({ $tone }) => $tone?.color || '#495057'};
  font-size: 12px;
  font-weight: 700;
  text-transform: capitalize;
`;

export const DetailGridStyle = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 18px 0;
`;

export const DetailCardStyle = styled.div`
  padding: 14px;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 18px rgba(33, 37, 41, 0.04);
`;

export const SectionHeaderStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 22px 0 10px;
`;

export const VerdictSummaryStyle = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const VerdictPillStyle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border: 1px solid ${({ $tone, $active }) => ($active ? $tone?.color : $tone?.border) || '#dee2e6'};
  border-radius: 999px;
  background: ${({ $tone }) => $tone?.background || '#f1f3f5'};
  color: ${({ $tone }) => $tone?.color || '#495057'};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
  box-shadow: ${({ $active, $tone }) =>
    $active ? `0 0 0 2px ${$tone?.border || '#dee2e6'}` : 'none'};

  &:hover:not(:disabled) {
    filter: brightness(0.98);
  }
`;

export const EvidenceFilterMetaStyle = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
  color: #868e96;
  font-size: 11px;
  line-height: 16px;
`;

export const EvidenceListStyle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
`;

export const EvidenceItemStyle = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${({ $tone }) => $tone?.border || '#e9ecef'};
  border-radius: 12px;
  background: #fff;
  color: #25262b;
  font-size: 12px;
  line-height: 18px;
  box-shadow: 0 4px 14px rgba(33, 37, 41, 0.035);

  & > * {
    min-width: 0;
  }
`;

export const EvidenceHeaderStyle = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

export const EvidenceBadgeGroupStyle = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

export const EvidenceCategoryBadgeStyle = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 4px 8px;
  border-radius: 999px;
  background: #edf2ff;
  color: #364fc7;
  font-size: 11px;
  font-weight: 700;
`;

export const FieldConfidenceBadgeStyle = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${({ $score }) => ($score >= 85 ? '#EBFBEE' : $score >= 70 ? '#FFF4E6' : '#FFF5F5')};
  color: ${({ $score }) => ($score >= 85 ? '#2F9E44' : $score >= 70 ? '#F08C00' : '#E03131')};
  font-size: 11px;
  font-weight: 800;
`;

export const EvidenceValueStyle = styled.div`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #f1f3f5;
  border-radius: 10px;
  background: #fbfcfd;
  margin-bottom: 8px;

  & > * {
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

export const EvidenceReasonStyle = styled.div`
  padding-top: 10px;
  border-top: 1px solid #f1f3f5;
  color: #495057;
  font-size: 12px;
  line-height: 18px;
`;

export const SourceReferenceStyle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SourceMatchedValueStyle = styled.div`
  width: fit-content;
  padding: 6px 8px;
  border: 1px solid #b2f2bb;
  border-radius: 8px;
  background: #ebfbee;
  color: #2b8a3e;
  font-size: 12px;
  font-weight: 700;
`;

export const SourceTableStyle = styled.div`
  display: grid;
  grid-template-columns: 70px 80px minmax(120px, 1fr);
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  max-width: 100%;
`;

export const SourceTableCellStyle = styled.div`
  padding: 6px 8px;
  border-bottom: 1px solid #f1f3f5;
  border-right: 1px solid #f1f3f5;
  color: ${({ $header }) => ($header ? '#868E96' : '#25262B')};
  font-size: 11px;
  font-weight: ${({ $header }) => ($header ? 700 : 400)};
  line-height: 16px;
  overflow-wrap: anywhere;
`;

export const NoteItemStyle = styled.div`
  padding: 10px 12px;
  border: 1px solid #fff3bf;
  border-radius: 10px;
  background: #fff9db;
  color: #5c3c00;
  font-size: 12px;
  line-height: 18px;
`;

export const FileNameStyle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const FooterStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  margin-top: 32px;

  button:disabled {
    svg {
      path {
        fill: transparent !important;
      }
    }
  }
`;

export const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    overflow: 'hidden',
    // Stay above the page table's sticky <thead> (z-index 9 in shared Table).
    zIndex: 1001
  },
  overlay: {
    background: 'rgba(0, 0, 0, 0.30)',
    zIndex: 1000
  }
};

export const detailsModalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    overflow: 'hidden',
    border: '0',
    borderRadius: '18px',
    padding: '20px',
    zIndex: 1003
  },
  overlay: {
    background: 'rgba(0, 0, 0, 0.30)',
    zIndex: 1002
  }
};

export const colors = {
  Completed: '#2F9E44',
  'Not started': '#868E96',
  'In progress': '#FD7E14',
  Cancelled: '#868E96',
  Failed: '#E03131',
  'Not extracted': '#868E96'
};

export const CancelButton = styled(Button)`
  width: 159px;
  background: #fff;
  border: 1px solid #e03131;
  color: #e03131;
`;
